<script setup lang="ts">
    import type { AccordeonElementStoryblok } from '~storyblok-types/AccordeonElementStoryblok';
    import type { GFlexibleGridStoryblok } from '~storyblok-types/GFlexibleGridStoryblok';
    import type { AccordeonEntryStoryblok } from '~storyblok-types/AccordeonEntryStoryblok';

    type AccordeonEntryContentBlok = AccordeonElementStoryblok | GFlexibleGridStoryblok;

    const props = defineProps<{
        blok: AccordeonEntryStoryblok;
        isOpen?: boolean;
        allowOnlyOneOpen?: boolean;
    }>();

    const emit = defineEmits<{
        toggle: [];
    }>();

    const isOpenLocal = ref(props.isOpen ?? false);
    const content = computed<AccordeonEntryContentBlok[]>(() => props.blok.content ?? []);
    const panelId = computed(() => `accordeon-panel-${props.blok._uid}`);
    const buttonId = computed(() => `accordeon-trigger-${props.blok._uid}`);
    const isEntryOpen = computed(() => (props.allowOnlyOneOpen ? (props.isOpen ?? false) : isOpenLocal.value));
    const hoverColorClass = computed(() => {
        switch (props.blok.headerHoverColor) {
            case 'beige':
                return 'hover-color-beige';
            case 'blue':
                return 'hover-color-blue';
            case 'none':
                return 'hover-color-none';
            default:
                return 'hover-color-default';
        }
    });

    watch(
        [() => props.allowOnlyOneOpen, () => props.isOpen],
        ([allowOnlyOneOpen, isOpenValue]) => {
            if (allowOnlyOneOpen) {
                isOpenLocal.value = isOpenValue ?? false;
            }
        },
        { immediate: true }
    );

    const toggleElement = () => {
        if (props.allowOnlyOneOpen) {
            emit('toggle');
        } else {
            isOpenLocal.value = !isOpenLocal.value;
        }
    };
</script>

<template>
    <div
        v-editable="props.blok"
        class="element-container"
        :class="{ '--open': isEntryOpen }"
    >
        <button
            :id="buttonId"
            :aria-controls="panelId"
            :aria-expanded="isEntryOpen"
            class="accordeon-question"
            :class="hoverColorClass"
            type="button"
            @click="toggleElement"
        >
            <h3>{{ props.blok.headline }}</h3>
            <div class="accordeon-button">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-plus"
                >
                    <path d="M5 12h14" />
                    <Transition name="cross">
                        <path
                            v-if="!isEntryOpen"
                            d="M12 5v14"
                        />
                    </Transition>
                </svg>
            </div>
        </button>

        <Transition name="accordeon">
            <div
                v-show="isEntryOpen"
                :id="panelId"
                :aria-labelledby="buttonId"
                class="accordeon-answer"
                role="region"
            >
                <div class="accordeon-entry-content">
                    <StoryblokComponentWrapper
                        v-for="contentBlok in content"
                        :key="contentBlok._uid"
                        :blok="contentBlok"
                    />
                </div>
            </div>
        </Transition>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/basics/accordeon.scss';
</style>
